import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { readLinkProfileSession } from '../auth/session';
import { formatOnTimeRoleLabel } from '../utils/access';
import {
  demoProducts,
  formatMoney,
  getCustomerDocId,
  normalizePhone,
} from '../lib/intakeCatalog';
import {
  evaluateRouting,
  findNearestStore,
  getStoreByName,
  loadOnTimeSettings,
} from '../lib/ontimeSettings';

import { logIntakeCreatedEvent } from '../lib/actions';


function activeLinkActor(user, claims) {
  const profileSession = readLinkProfileSession();
  const activeClaims = profileSession?.claims || claims || {};
  const name = String(
    profileSession?.profileName ||
    activeClaims.displayName ||
    activeClaims.fullName ||
    activeClaims.name ||
    user?.displayName ||
    user?.email ||
    'Link user'
  ).trim();
  const claimRole = claims?.onTimeRole || claims?.role || '';
  const sessionRole = activeClaims.onTimeRole || activeClaims.role || '';
  const role = ['admin', 'owner'].includes(String(claimRole || '').toLowerCase()) ? claimRole : (sessionRole || claimRole || 'user');
  const roleLabel = ['admin', 'owner'].includes(String(claimRole || '').toLowerCase())
    ? formatOnTimeRoleLabel(claimRole)
    : (activeClaims.onTimeRoleLabel || activeClaims.roleLabel || formatOnTimeRoleLabel(role));
  return {
    uid: user?.uid || activeClaims.uid || '',
    email: String(user?.email || activeClaims.email || '').trim().toLowerCase(),
    name,
    displayName: name,
    role,
    roleLabel,
    profileId: profileSession?.profileId || activeClaims.profileId || '',
  };
}

const inputStyle = {
  width: '100%',
  background: '#0b1727',
  color: '#e7f4ff',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 14,
  padding: '12px 14px',
  outline: 'none',
  boxSizing: 'border-box',
};

function money(v) {
  return `R ${Number(v || 0).toFixed(2)}`;
}

function blankForm() {
  return {
    orderNumber: '',
    firstName: '',
    surname: '',
    phone: '',
    suburb: '',
    address: '',
    notes: '',
    source: 'Website manual capture',
    paymentMethod: 'card_paid',
  };
}

function slug(v = '') {
  return String(v).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'item';
}

function formatDateLabel(value) {
  if (!value) return '—';
  try {
    if (typeof value?.toDate === 'function') return value.toDate().toLocaleDateString();
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

function cleanText(value = '') {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function scrollPageTop() {
  try {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch {
    window.scrollTo(0, 0);
  }
}

function extractCustomerGeo(customer) {
  const pairs = [
    [customer?.geo?.lat, customer?.geo?.lng],
    [customer?.geo?.latitude, customer?.geo?.longitude],
    [customer?.location?.lat, customer?.location?.lng],
    [customer?.location?.latitude, customer?.location?.longitude],
    [customer?.lat, customer?.lng],
    [customer?.latitude, customer?.longitude],
  ];
  for (const [lat, lng] of pairs) {
    const nlat = Number(lat);
    const nlng = Number(lng);
    if (Number.isFinite(nlat) && Number.isFinite(nlng)) return { lat: nlat, lng: nlng };
  }
  return null;
}

function buildCustomerAddressText(customer) {
  return [customer?.addressLine1, customer?.address, customer?.fullAddress, customer?.suburb, customer?.city]
    .filter(Boolean)
    .join(', ');
}

function matchStoreFromText(settings, ...parts) {
  const hay = cleanText(parts.filter(Boolean).join(' '));
  if (!hay) return null;
  const stores = (settings?.stores || []).filter((store) => store.active && store.geo);
  return stores.find((store) => {
    const name = cleanText(store.name);
    return name && hay.includes(name);
  }) || null;
}

function AddressAutocompleteInput({ value, onChange, onSelect, placeholder }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const serviceRef = useRef(null);
  const geocoderRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!window.google?.maps?.places?.AutocompleteService || serviceRef.current) return;
    serviceRef.current = new window.google.maps.places.AutocompleteService();
    geocoderRef.current = new window.google.maps.Geocoder();
  }, []);

  useEffect(() => {
    const term = String(value || '').trim();
    if (!term || !serviceRef.current) {
      setItems([]);
      return;
    }
    const current = ++reqRef.current;
    serviceRef.current.getPlacePredictions({ input: term, componentRestrictions: { country: 'za' } }, (predictions = [], status) => {
      if (current !== reqRef.current) return;
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
        setItems([]);
        return;
      }
      setItems(predictions.slice(0, 5));
    });
  }, [value]);

  function choose(prediction) {
    if (!geocoderRef.current) {
      onSelect?.({ address: prediction.description, suburb: '', geo: null, placeId: prediction.place_id });
      setOpen(false);
      setItems([]);
      return;
    }
    geocoderRef.current.geocode({ placeId: prediction.place_id }, (results = [], status) => {
      const top = results[0];
      const comps = top?.address_components || [];
      const suburb = comps.find((c) => c.types.includes('sublocality') || c.types.includes('locality') || c.types.includes('sublocality_level_1'))?.long_name || '';
      const geo = top?.geometry?.location
        ? { lat: Number(top.geometry.location.lat()), lng: Number(top.geometry.location.lng()) }
        : null;
      onSelect?.({
        address: top?.formatted_address || prediction.description,
        suburb,
        geo,
        placeId: prediction.place_id,
        status,
      });
      setOpen(false);
      setItems([]);
    });
  }

  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={inputStyle}
      />
      {open && items.length ? (
        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl shadow-black/40">
          {items.map((item) => (
            <button
              key={item.place_id}
              type="button"
              onClick={() => choose(item)}
              className="block w-full border-b border-slate-200 px-4 py-3 text-left text-sm text-slate-800 hover:bg-slate-100 last:border-b-0"
            >
              <div className="font-medium">{item.structured_formatting?.main_text || item.description}</div>
              <div className="text-xs text-slate-500">{item.description}</div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function OnTimeIntakePage() {
  const { claims, user } = useAuth();
  const orgId = claims?.orgId || '';
  const actor = useMemo(() => activeLinkActor(user, claims), [user, claims]);
  const actorName = actor.name;
  const topRef = useRef(null);
  const manualStoreRef = useRef(false);

  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerGeo, setCustomerGeo] = useState(null);
  const [customerPlaceId, setCustomerPlaceId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankForm());
  const [selectedStore, setSelectedStore] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [pendingOverwrite, setPendingOverwrite] = useState(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    loadOnTimeSettings(orgId).then((s) => {
      setSettings(s);
      const activeStores = s.stores.filter((store) => store.active);
      setSelectedStore(activeStores[0]?.name || s.stores[0]?.name || '');
      setForm((prev) => ({
        ...prev,
        source: s.sources[0] || prev.source,
        paymentMethod: s.paymentMethods[0]?.code || prev.paymentMethod,
      }));
    }).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const unsubProducts = onSnapshot(
      query(collection(db, 'orgs', orgId, 'ontimeProducts'), orderBy('name')),
      (snap) => setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.active !== false && p.name)),
      () => setProducts(demoProducts)
    );
    const unsubCustomers = onSnapshot(
      query(collection(db, 'orgs', orgId, 'ontimeCustomers'), orderBy('updatedAt', 'desc')),
      (snap) => setCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setCustomers([])
    );
    return () => {
      unsubProducts();
      unsubCustomers();
    };
  }, [orgId]);

  const activeStores = useMemo(() => (settings?.stores || []).filter((store) => store.active), [settings]);
  const selectedStoreConfig = useMemo(() => getStoreByName(settings || {}, selectedStore), [settings, selectedStore]);
  const paymentOptions = settings?.paymentMethods || [];
  const sourceOptions = settings?.sources || [];

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return [];
    if (selectedCustomerId && customers.find((customer) => customer.id === selectedCustomerId)) return [];
    return customers.filter((customer) => {
      const hay = [customer.firstName, customer.surname, customer.name, customer.phone, customer.suburb, customer.addressLine1]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    }).slice(0, 6);
  }, [customerSearch, customers]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return [];
    return (products.length ? products : demoProducts).filter((product) => {
      const hay = [product.name, product.sku, product.category].join(' ').toLowerCase();
      return hay.includes(q);
    }).slice(0, 8);
  }, [productSearch, products]);

  const selectedCustomer = useMemo(() => customers.find((customer) => customer.id === selectedCustomerId) || null, [customers, selectedCustomerId]);

  const routing = useMemo(() => {
    if (!settings || !customerGeo) return null;
    return evaluateRouting(settings, selectedStore, customerGeo);
  }, [settings, selectedStore, customerGeo]);

  const subtotal = useMemo(() => items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0), [items]);
  const deliveryFee = Number(selectedStoreConfig?.deliveryFee ?? settings?.defaultDeliveryFee ?? 0);
  const total = subtotal + deliveryFee;

  useEffect(() => {
    if (!settings || manualStoreRef.current) return;
    if (customerGeo) {
      applyNearestStoreFromGeo(customerGeo, [form.address, form.suburb].filter(Boolean).join(', '));
      return;
    }
    const fallbackAddress = [form.address, form.suburb].filter(Boolean).join(', ');
    const matched = matchStoreFromText(settings, fallbackAddress);
    if (matched?.name && matched.name !== selectedStore) {
      setSelectedStore(matched.name);
      return;
    }
    if (!fallbackAddress) return;
    let alive = true;
    geocodeAddressText(fallbackAddress).then((result) => {
      if (!alive || !result?.geo) return;
      setCustomerGeo(result.geo);
      setCustomerPlaceId((prev) => prev || result.placeId || '');
      applyNearestStoreFromGeo(result.geo, fallbackAddress);
    });
    return () => { alive = false; };
  }, [settings, customerGeo, form.address, form.suburb, selectedStore]);

  async function geocodeAddressText(address) {
    if (!address || !window.google?.maps?.Geocoder) return null;
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      const query = /south africa/i.test(address) ? address : `${address}, South Africa`;
      geocoder.geocode({ address: query, componentRestrictions: { country: 'ZA' } }, (results = [], status) => {
        const top = results[0];
        if (!top?.geometry?.location) {
          resolve(null);
          return;
        }
        const comps = top.address_components || [];
        const suburb = comps.find((c) => c.types.includes('sublocality') || c.types.includes('locality') || c.types.includes('sublocality_level_1'))?.long_name || '';
        resolve({
          address: top.formatted_address || address,
          suburb,
          geo: { lat: Number(top.geometry.location.lat()), lng: Number(top.geometry.location.lng()) },
          placeId: top.place_id || '',
        });
      });
    });
  }


  function applyNearestStoreFromGeo(geo, fallbackText = '') {
    if (!settings || manualStoreRef.current) return;
    const nearest = geo ? findNearestStore(settings, geo) : null;
    if (nearest?.name) {
      if (nearest.name !== selectedStore) setSelectedStore(nearest.name);
      return;
    }
    const matched = matchStoreFromText(settings, fallbackText, form.suburb, form.address);
    if (matched?.name && matched.name !== selectedStore) setSelectedStore(matched.name);
  }

  async function hydrateCustomerLocation(customerLike, options = {}) {
    const fallbackText = buildCustomerAddressText(customerLike) || [form.address, form.suburb].filter(Boolean).join(', ');
    const existingGeo = extractCustomerGeo(customerLike);
    if (existingGeo) {
      setCustomerGeo(existingGeo);
      setCustomerPlaceId((customerLike?.addressPlaceId || customerLike?.placeId || '').trim());
      applyNearestStoreFromGeo(existingGeo, fallbackText);
      return { geo: existingGeo, source: 'stored' };
    }

    const matchedStore = matchStoreFromText(settings, fallbackText, customerLike?.suburb, customerLike?.addressLine1, customerLike?.address);
    if (matchedStore?.geo) {
      const approx = { lat: Number(matchedStore.geo.lat), lng: Number(matchedStore.geo.lng) };
      setCustomerGeo(approx);
      applyNearestStoreFromGeo(approx, fallbackText);
      if (options.skipGeocode !== true) {
        geocodeAddressText(fallbackText).then((result) => {
          if (!result?.geo) return;
          setCustomerGeo(result.geo);
          setCustomerPlaceId((prev) => prev || result.placeId || '');
          if (result.address || result.suburb) {
            setForm((prev) => ({
              ...prev,
              address: result.address || prev.address,
              suburb: result.suburb || prev.suburb,
            }));
          }
          applyNearestStoreFromGeo(result.geo, fallbackText);
        });
      }
      return { geo: approx, source: 'matched-store' };
    }

    if (!fallbackText) return { geo: null, source: 'none' };
    const result = await geocodeAddressText(fallbackText);
    if (result?.geo) {
      setCustomerGeo(result.geo);
      setCustomerPlaceId(result.placeId || '');
      if (result.address || result.suburb) {
        setForm((prev) => ({
          ...prev,
          address: result.address || prev.address,
          suburb: result.suburb || prev.suburb,
        }));
      }
      applyNearestStoreFromGeo(result.geo, fallbackText);
      return { geo: result.geo, source: 'geocoded' };
    }
    return { geo: null, source: 'none' };
  }

  useEffect(() => {
    if (!selectedCustomer) return;
    hydrateCustomerLocation(selectedCustomer);
  }, [selectedCustomerId, selectedCustomer, settings]);

  function resetAll() {
    manualStoreRef.current = false;
    setSelectedCustomerId('');
    setCustomerSearch('');
    setCustomerGeo(null);
    setCustomerPlaceId('');
    setProductSearch('');
    setItems([]);
    setPendingOverwrite(null);
    setShowOverwriteConfirm(false);
    setForm((prev) => ({
      ...blankForm(),
      source: sourceOptions[0] || prev.source,
      paymentMethod: paymentOptions[0]?.code || prev.paymentMethod,
    }));
    if (activeStores[0]?.name) setSelectedStore(activeStores[0].name);
  }

  function applyCustomer(customer) {
    manualStoreRef.current = false;
    setSelectedCustomerId(customer.id);
    setCustomerSearch(`${customer.firstName || ''} ${customer.surname || customer.name || ''}`.trim());
    setForm((prev) => ({
      ...prev,
      firstName: customer.firstName || customer.name?.split(' ')[0] || '',
      surname: customer.surname || customer.name?.split(' ').slice(1).join(' ') || '',
      phone: customer.phone || '',
      suburb: customer.suburb || '',
      address: customer.addressLine1 || customer.address || customer.fullAddress || '',
      notes: customer.notes || '',
    }));
    setCustomerPlaceId((customer.addressPlaceId || customer.placeId || '').trim());
    setCustomerGeo(null);
    hydrateCustomerLocation(customer);
  }

  function buildQtyOptions(product) {
    const baseQty = Number(product.baseQty ?? 1);
    const fromRules = Array.isArray(product.priceRules) ? product.priceRules.map((r) => Number(r.qty)).filter((q) => Number.isFinite(q)) : [];
    return Array.from(new Set([baseQty, ...fromRules])).sort((a, b) => a - b);
  }

  function priceForQty(product, qty) {
    const parsedQty = Number(qty || product.baseQty || 1);
    const baseQty = Number(product.baseQty ?? 1);
    const basePrice = Number(product.basePrice || 0);
    const rule = [...(product.priceRules || [])]
      .map((r) => ({ qty: Number(r.qty), price: Number(r.price) }))
      .find((r) => r.qty === parsedQty);
    const totalPrice = rule ? rule.price : (baseQty ? (basePrice / baseQty) * parsedQty : basePrice * parsedQty);
    return {
      qty: parsedQty,
      totalPrice: Number(totalPrice || 0),
      unitLabel: `${parsedQty}`,
    };
  }

  function addProduct(product) {
    const opts = buildQtyOptions(product);
    const qty = opts[0] || Number(product.baseQty || 1);
    const pricing = priceForQty(product, qty);
    const next = {
      lineId: `line_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      productId: product.id,
      name: product.name,
      sku: product.sku || '',
      qty,
      qtyOptions: opts,
      unitPrice: pricing.totalPrice / (qty || 1),
      lineTotal: pricing.totalPrice,
    };
    setItems((prev) => [next, ...prev]);
    setProductSearch('');
  }

  function updateItemQty(lineId, qty) {
    setItems((prev) => prev.map((item) => {
      if (item.lineId !== lineId) return item;
      const product = (products.length ? products : demoProducts).find((p) => p.id === item.productId);
      if (!product) return item;
      const pricing = priceForQty(product, qty);
      return { ...item, qty: Number(qty), lineTotal: pricing.totalPrice, unitPrice: pricing.totalPrice / (Number(qty) || 1) };
    }));
  }

  function removeItem(lineId) {
    setItems((prev) => prev.filter((item) => item.lineId !== lineId));
  }

  async function submitOrder({ forceOverwrite = false, effectiveGeoOverride = null, routeCheckOverride = null } = {}) {
    if (!orgId) {
      setError('Missing org-linked login.');
      scrollPageTop();
      return;
    }
    if (!form.firstName.trim()) {
      setError('Select a customer or enter customer details before creating the order. First name is required.');
      scrollPageTop();
      return;
    }
    if (!form.phone.trim()) {
      setError('Phone / WhatsApp is required before creating the order.');
      scrollPageTop();
      return;
    }
    if (!form.address.trim()) {
      setError('Delivery address is required before creating the order.');
      scrollPageTop();
      return;
    }
    if (!items.length) {
      setError('Add at least one item.');
      scrollPageTop();
      return;
    }
    if (!selectedStore) {
      setError('Select a store.');
      scrollPageTop();
      return;
    }

    let effectiveGeo = effectiveGeoOverride || customerGeo;
    if (!effectiveGeo) {
      const hydrated = await hydrateCustomerLocation({
        ...selectedCustomer,
        suburb: form.suburb,
        addressLine1: form.address,
        address: form.address,
        addressPlaceId: customerPlaceId,
        geo: customerGeo,
      }, { skipGeocode: false });
      effectiveGeo = hydrated?.geo || null;
    }

    if (!effectiveGeo) {
      setError('Pick the customer address from Google suggestions, or reselect the customer so routing can calculate.');
      scrollPageTop();
      return;
    }

    const routeCheck = routeCheckOverride || evaluateRouting(settings || {}, selectedStore, effectiveGeo);
    if (routeCheck.withinRange === false && !forceOverwrite) {
      const suggestion = routeCheck.nearestValidStore ? ` Closest valid store: ${routeCheck.nearestValidStore.name} (${routeCheck.nearestValidStore.distanceKm.toFixed(1)} km).` : '';
      setPendingOverwrite({ routeCheck, effectiveGeo });
      setError(`${selectedStore} is too far for this address (${routeCheck.distanceKm.toFixed(1)} km vs ${Number(routeCheck.selectedStore?.maxKm || 0).toFixed(1)} km allowed).${suggestion}`);
      scrollPageTop();
      return;
    }

    setSubmitting(true);
    const customerId = selectedCustomerId || getCustomerDocId(form.phone) || `cust_${Date.now()}`;
    const orderNumber = form.orderNumber.trim() || `OT-${Date.now()}`;
    const fullName = `${form.firstName} ${form.surname}`.trim();
    const paymentLabel = paymentOptions.find((item) => item.code === form.paymentMethod)?.label || form.paymentMethod;

    const customerStats = selectedCustomer?.stats || {};
    try {
      await setDoc(doc(db, 'orgs', orgId, 'ontimeCustomers', customerId), {
        firstName: form.firstName.trim(),
        surname: form.surname.trim(),
        name: fullName,
        phone: form.phone.trim(),
        normalizedPhone: normalizePhone(form.phone),
        suburb: form.suburb.trim(),
        addressLine1: form.address.trim(),
        addressPlaceId: customerPlaceId || '',
        geo: effectiveGeo,
        notes: form.notes.trim(),
        updatedAt: serverTimestamp(),
        createdAt: selectedCustomer?.createdAt || serverTimestamp(),
        stats: {
          orderCount: Number(customerStats.orderCount || 0) + 1,
          totalSpend: Number(customerStats.totalSpend || 0) + total,
          lastOrderAt: serverTimestamp(),
          lastOrderNumber: orderNumber,
        },
      }, { merge: true });

      const orderRef = await addDoc(collection(db, 'orgs', orgId, 'ontimeOrders'), {
        orderNumber,
        source: form.source,
        stage: 'new',
        store: selectedStore,
        customerId,
        customer: {
          firstName: form.firstName.trim(),
          surname: form.surname.trim(),
          name: fullName,
          phone: form.phone.trim(),
          suburb: form.suburb.trim(),
          addressLine1: form.address.trim(),
          addressPlaceId: customerPlaceId || '',
          geo: effectiveGeo,
        },
        pricing: { subtotal, deliveryFee, total },
        payment: { method: form.paymentMethod, label: paymentLabel },
        sentByName: actor.name,
        sentByRole: actor.role,
        sentByRoleLabel: actor.roleLabel,
        senderName: actor.name,
        senderRole: actor.role,
        senderRoleLabel: actor.roleLabel,
        createdByName: actor.name,
        createdByDisplayName: actor.name,
        createdByRoleLabel: actor.roleLabel,
        createdBy: { uid: actor.uid, email: actor.email, name: actor.name, displayName: actor.name, role: actor.role, roleLabel: actor.roleLabel, profileId: actor.profileId },
        items: items.map((item) => ({
          productId: item.productId,
          name: item.name,
          sku: item.sku,
          qty: item.qty,
          unitPrice: item.unitPrice,
          lineTotal: item.lineTotal,
        })),
        notes: { online: form.notes.trim() },
        routing: {
          selectedStore,
          customerGeo: effectiveGeo,
          distanceKm: routeCheck.distanceKm,
          nearestStore: routeCheck.nearestStore?.name || '',
          overwrite: forceOverwrite ? {
            at: serverTimestamp(),
            byUid: user?.uid || '',
            byName: actorName,
            selectedStore,
            distanceKm: Number(routeCheck.distanceKm || 0),
            allowedKm: Number(routeCheck.selectedStore?.maxKm || 0),
            suggestedStore: routeCheck.nearestValidStore?.name || routeCheck.nearestStore?.name || '',
          } : null,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await logIntakeCreatedEvent({
        orgId,
        orderId: orderRef.id,
        orderNumber,
        actor: { userId: actor.uid, name: actor.name, role: actor.role, roleLabel: actor.roleLabel },
        meta: {
          source: form.source,
          selectedStore,
          routingOverwrite: forceOverwrite ? {
            selectedStore,
            distanceKm: Number(routeCheck.distanceKm || 0),
            allowedKm: Number(routeCheck.selectedStore?.maxKm || 0),
            suggestedStore: routeCheck.nearestValidStore?.name || routeCheck.nearestStore?.name || '',
            byName: actorName,
          } : null,
        },
      });

      setPendingOverwrite(null);
      setShowOverwriteConfirm(false);
      setMessage(`Order ${orderNumber} created successfully.`);
      resetAll();
      scrollPageTop();
    } catch (err) {
      setError(err?.message || 'Failed to create order.');
      scrollPageTop();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setMessage('');
    setError('');
    setPendingOverwrite(null);
    await submitOrder();
  }

  const nearestText = routing?.nearestStore ? `${routing.nearestStore.name} (${routing.nearestStore.distanceKm.toFixed(1)} km).` : '—';

  return (
    <div className="link-intake-page space-y-6 pt-0" ref={topRef}>
      {message ? <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">{message}</div> : null}
      {error ? (
        <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          <div className="flex items-center justify-between gap-3">
            <div>{error}</div>
            {pendingOverwrite?.routeCheck?.withinRange === false ? (
              <button type="button" onClick={() => setShowOverwriteConfirm(true)} className="shrink-0 rounded-xl border border-amber-300/30 bg-amber-500/15 px-3 py-1.5 text-xs font-medium text-amber-100 hover:bg-amber-500/25">Overwrite</button>
            ) : null}
          </div>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="link-intake-grid grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="link-intake-command-panel space-y-6 rounded-3xl border border-white/10 bg-white/5 p-5">
          <section>
            <div className="mb-3 flex items-center justify-between text-xs uppercase tracking-[0.22em] text-slate-500">
              <span>Customer search</span>
              <div className="flex items-center gap-2">
                {selectedCustomer ? <span className="text-cyan-200">{selectedCustomer.stats?.orderCount || 0} orders • {money(selectedCustomer.stats?.totalSpend || 0)} total</span> : null}
                {(selectedCustomerId || form.firstName || items.length || form.orderNumber) ? (
                  <button type="button" onClick={resetAll} className="rounded-xl border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white hover:bg-white/10">Clear</button>
                ) : null}
              </div>
            </div>
            <input value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="Search by phone, name, suburb or last order" style={inputStyle} />
            {customerSearch.trim() && filteredCustomers.length ? (
              <div className="link-customer-results mt-3 space-y-2 rounded-3xl border border-cyan-400/20 bg-cyan-500/10 p-2">
                {filteredCustomers.map((customer) => (
                  <button key={customer.id} type="button" onClick={() => applyCustomer(customer)} className="link-customer-result-card flex w-full items-start justify-between rounded-2xl border border-cyan-400/20 bg-[#062336] px-4 py-3 text-left text-sm text-slate-200 hover:border-cyan-300/40">
                    <div>
                      <div className="font-semibold text-white">{customer.name || `${customer.firstName || ''} ${customer.surname || ''}`.trim() || 'Customer'}</div>
                      <div className="mt-1 text-slate-300">{customer.phone || 'No phone'} • {customer.suburb || 'No suburb'}</div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-300">
                        <span className="rounded-full bg-white/5 px-2 py-1">{money(customer.stats?.totalSpend || 0)} total</span>
                        <span className="rounded-full bg-white/5 px-2 py-1">Last {customer.stats?.lastOrderNumber || '—'}</span>
                        <span className="rounded-full bg-white/5 px-2 py-1">Updated {formatDateLabel(customer.updatedAt)}</span>
                      </div>
                    </div>
                    <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-white">{customer.stats?.orderCount || 0} orders</span>
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section>
            <div className="mb-3 text-xs uppercase tracking-[0.22em] text-slate-500">Order</div>
            <div className="grid gap-3 md:grid-cols-[190px_minmax(0,1fr)]">
              <input value={form.orderNumber} onChange={(e) => setForm((prev) => ({ ...prev, orderNumber: e.target.value }))} placeholder="Order Number" style={inputStyle} />
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-400">Capture the order number exactly as shown on the online screenshot or checkout.</div>
            </div>
          </section>

          <section>
            <div className="mb-3 text-xs uppercase tracking-[0.22em] text-slate-500">Customer</div>
            <div className="grid gap-3 md:grid-cols-2">
              <input value={form.firstName} onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))} placeholder="First name" style={inputStyle} />
              <input value={form.surname} onChange={(e) => setForm((prev) => ({ ...prev, surname: e.target.value }))} placeholder="Surname" style={inputStyle} />
              <input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} placeholder="Phone / WhatsApp" style={inputStyle} />
              <input value={form.suburb} onChange={(e) => setForm((prev) => ({ ...prev, suburb: e.target.value }))} placeholder="Suburb" style={inputStyle} />
            </div>
            <div className="mt-3">
              <AddressAutocompleteInput
                value={form.address}
                onChange={(value) => {
                  manualStoreRef.current = false;
                  setForm((prev) => ({ ...prev, address: value }));
                  setCustomerGeo(null);
                  setCustomerPlaceId('');
                }}
                onSelect={({ address, suburb, geo, placeId }) => {
                  manualStoreRef.current = false;
                  setForm((prev) => ({ ...prev, address, suburb: suburb || prev.suburb }));
                  setCustomerGeo(geo || null);
                  setCustomerPlaceId(placeId || '');
                }}
                placeholder="Full delivery address"
              />
              <div className="mt-2 text-xs text-slate-500">Google address suggestions are live. Selecting one will also try to fill suburb and save map coordinates for drivers later.</div>
            </div>
            <textarea value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Notes, access instructions, suggested swap, etc." rows={4} style={{ ...inputStyle, marginTop: 12, resize: 'vertical' }} />
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between text-xs uppercase tracking-[0.22em] text-slate-500">
              <span>Items</span>
              <span>{items.length} selected</span>
            </div>
            <div>
              <input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && filteredProducts.length) {
                    e.preventDefault();
                    addProduct(filteredProducts[0]);
                  }
                }}
                placeholder="Search product and select"
                style={inputStyle}
              />
              <div className="mt-2 text-xs text-slate-500">Type to search the live catalog, then select a product to add it below. Press Enter to add the top result.</div>
              {filteredProducts.length ? (
                <div className="mt-2 space-y-2 rounded-2xl border border-white/10 bg-[#081321] p-2">
                  {filteredProducts.map((product) => (
                    <button key={product.id} type="button" onClick={() => addProduct(product)} className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/5">
                      <div>
                        <div className="font-medium text-white">{product.name}</div>
                        <div className="text-slate-400">{product.sku || 'No sku'}</div>
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white">{money(product.basePrice || 0)}</div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="mt-3 space-y-3">
              {items.length ? items.map((item, index) => (
                <div key={item.lineId} className="rounded-2xl border border-white/10 bg-[#0b1727] p-3">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-white">{index + 1}. {item.name}</div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-cyan-200">
                        {item.sku ? <span className="rounded-full bg-white/5 px-2 py-1 text-slate-300">{item.sku}</span> : null}
                        <span className="rounded-full bg-cyan-500/10 px-2 py-1">{item.qty} bundle</span>
                        <span className="rounded-full bg-white/5 px-2 py-1 text-slate-300">{money(item.lineTotal)} for {item.qty}</span>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeItem(item.lineId)} className="rounded-xl border border-rose-400/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">Remove</button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[160px_minmax(0,1fr)]">
                    <select value={item.qty} onChange={(e) => updateItemQty(item.lineId, Number(e.target.value))} style={inputStyle}>
                      {item.qtyOptions.map((qty) => <option key={qty} value={qty}>{qty}</option>)}
                    </select>
                    <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-300">
                      <span>Line total</span>
                      <strong className="text-white">{money(item.lineTotal)}</strong>
                    </div>
                  </div>
                </div>
              )) : <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-4 text-sm text-slate-500">Search and select products above to add order lines.</div>}
            </div>
          </section>
        </div>

        <div className="link-intake-side-stack space-y-5">
          <div className="link-intake-side-card rounded-3xl border border-white/10 bg-white/5 p-5">
            <div className="mb-3 text-xs uppercase tracking-[0.22em] text-slate-500">Routing</div>
            <div className="space-y-3">
              <select value={selectedStore} onChange={(e) => { manualStoreRef.current = true; setSelectedStore(e.target.value); }} style={inputStyle}>
                {activeStores.map((store) => <option key={store.id} value={store.name}>{store.name}</option>)}
              </select>
              {routing?.nearestStore ? <div className="text-xs text-cyan-200">Nearest store from this address: {nearestText}</div> : null}
              <select value={form.source} onChange={(e) => setForm((prev) => ({ ...prev, source: e.target.value }))} style={inputStyle}>
                {sourceOptions.map((source) => <option key={source}>{source}</option>)}
              </select>
              <select value={form.paymentMethod} onChange={(e) => setForm((prev) => ({ ...prev, paymentMethod: e.target.value }))} style={inputStyle}>
                {paymentOptions.map((method) => <option key={method.code} value={method.code}>{method.label}</option>)}
              </select>
              <div className="rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-3">
                <div className="flex items-center justify-between text-sm text-slate-300"><span>Delivery fee</span><strong className="text-white">{money(deliveryFee)}</strong></div>
                <div className="mt-1 text-xs text-cyan-200">Auto from settings for {selectedStore || 'selected store'}</div>
              </div>
              <div className={`rounded-2xl px-4 py-3 ${routing?.withinRange === false ? 'border border-amber-400/30 bg-amber-500/10' : 'border border-white/10 bg-[#0b1727]'}`}>
                <div className="flex items-center justify-between text-sm text-slate-300"><span>Routing check</span><strong className={routing?.withinRange === false ? 'text-amber-200' : 'text-cyan-200'}>{routing?.distanceKm != null ? `${routing.distanceKm.toFixed(1)} km` : (matchStoreFromText(settings, form.address, form.suburb)?.name ? 'Using suburb match' : 'Awaiting map pin')}</strong></div>
                <div className={`mt-2 text-xs ${routing?.withinRange === false ? 'text-amber-100' : 'text-slate-300'}`}>
                  {routing?.distanceKm != null
                    ? routing.withinRange === false
                      ? `${selectedStore} exceeds its ${(routing.selectedStore?.maxKm || 0).toFixed(1)} km limit.`
                      : `${selectedStore} is within its ${(routing.selectedStore?.maxKm || 0).toFixed(1)} km delivery radius.`
                    : matchStoreFromText(settings, form.address, form.suburb)?.name ? `Using ${matchStoreFromText(settings, form.address, form.suburb)?.name} as an address match while map pin resolves.` : 'Set both the store address in Settings and the customer address from Google suggestions to enable smart routing.'}
                </div>
                {routing?.nearestValidStore && routing.withinRange === false ? <div className="mt-2 text-xs text-amber-200">Suggested store: {routing.nearestValidStore.name}</div> : null}
              </div>
            </div>
          </div>

          <div className="link-intake-side-card rounded-3xl border border-white/10 bg-white/5 p-5">
            <div className="mb-3 text-xs uppercase tracking-[0.22em] text-slate-500">Customer profile</div>
            {selectedCustomer ? (
              <div className="space-y-3 text-sm text-slate-300">
                <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-4">
                  <div className="font-semibold text-white">{selectedCustomer.name || `${selectedCustomer.firstName || ''} ${selectedCustomer.surname || ''}`.trim()}</div>
                  <div className="mt-1 text-slate-300">{selectedCustomer.phone || 'No phone'} • {selectedCustomer.suburb || 'No suburb'}</div>
                  <div className="mt-2 text-cyan-100">{selectedCustomer.addressLine1 || 'No address'}</div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-[#0b1727] p-4"><div className="text-xs uppercase tracking-[0.2em] text-slate-500">Orders</div><div className="mt-2 text-2xl font-semibold text-white">{selectedCustomer.stats?.orderCount || 0}</div></div>
                  <div className="rounded-2xl border border-white/10 bg-[#0b1727] p-4"><div className="text-xs uppercase tracking-[0.2em] text-slate-500">Total spend</div><div className="mt-2 text-2xl font-semibold text-white">{money(selectedCustomer.stats?.totalSpend || 0)}</div></div>
                </div>
              </div>
            ) : <div className="text-sm text-slate-500">Select an existing customer or create a new one on submit.</div>}
          </div>

          <div className="link-intake-side-card rounded-3xl border border-white/10 bg-white/5 p-5">
            <div className="mb-3 text-xs uppercase tracking-[0.22em] text-slate-500">Summary</div>
            <div className="space-y-3 text-sm text-slate-300">
              <div className="flex items-center justify-between"><span>Customer</span><strong className="text-white">{`${form.firstName} ${form.surname}`.trim() || 'Not filled yet'}</strong></div>
              <div className="flex items-center justify-between"><span>Items</span><strong className="text-white">{items.length}</strong></div>
              <div className="flex items-center justify-between"><span>Payment</span><strong className="text-white">{paymentOptions.find((item) => item.code === form.paymentMethod)?.label || form.paymentMethod}</strong></div>
              <div className="rounded-2xl border border-white/10 bg-[#0b1727] p-3">
                <div className="mb-2 text-xs uppercase tracking-[0.2em] text-slate-500">Selected items</div>
                {items.length ? items.map((item) => (
                  <div key={item.lineId} className="mb-2 flex items-start justify-between gap-3 last:mb-0">
                    <div className="min-w-0 flex-1 text-slate-300">{item.qty} × {item.name}</div>
                    <div className="shrink-0 text-right font-semibold text-white">{money(item.lineTotal)}</div>
                  </div>
                )) : <div className="text-slate-500">No items selected yet.</div>}
              </div>
              <div className="flex items-center justify-between"><span>Subtotal</span><strong className="text-white">{money(subtotal)}</strong></div>
              <div className="flex items-center justify-between"><span>Delivery</span><strong className="text-white">{money(deliveryFee)}</strong></div>
              <div className="flex items-center justify-between border-t border-white/10 pt-3 text-base"><span>Total</span><strong className="text-cyan-200">{money(total)}</strong></div>
              <button disabled={submitting} type="submit" className="link-create-order-button w-full rounded-2xl border border-cyan-300/40 bg-cyan-500/20 px-4 py-3 font-bold text-cyan-50 disabled:cursor-not-allowed disabled:opacity-70">{submitting ? 'Creating order…' : 'Create Link order'}</button>
            </div>
          </div>
        </div>
      </form>

      {showOverwriteConfirm && pendingOverwrite?.routeCheck ? (
        <div className="link-modal-backdrop fixed inset-0 z-[80] flex items-center justify-center bg-[#020817]/80 px-4">
          <div className="link-modal-card w-full max-w-md rounded-3xl border border-amber-400/20 bg-[#07111f] p-5 shadow-2xl shadow-black/40">
            <div className="text-xs uppercase tracking-[0.22em] text-amber-200">Overwrite routing</div>
            <h3 className="mt-2 text-xl font-semibold text-white">Create this order anyway?</h3>
            <p className="mt-3 text-sm text-slate-300">{selectedStore} is outside its allowed radius for this customer. This overwrite will be recorded against the online team member profile.</p>
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-sm text-slate-300">
              <div className="flex items-center justify-between"><span>Distance</span><strong className="text-amber-200">{pendingOverwrite.routeCheck.distanceKm.toFixed(1)} km</strong></div>
              <div className="mt-2 flex items-center justify-between"><span>Allowed</span><strong className="text-white">{Number(pendingOverwrite.routeCheck.selectedStore?.maxKm || 0).toFixed(1)} km</strong></div>
              <div className="mt-2 flex items-center justify-between"><span>Suggested store</span><strong className="text-cyan-200">{pendingOverwrite.routeCheck.nearestValidStore?.name || pendingOverwrite.routeCheck.nearestStore?.name || '—'}</strong></div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setShowOverwriteConfirm(false)} className="link-modal-secondary rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10">Cancel</button>
              <button
                type="button"
                onClick={() => {
                  setShowOverwriteConfirm(false);
                  setMessage('');
                  submitOrder({ forceOverwrite: true, effectiveGeoOverride: pendingOverwrite.effectiveGeo, routeCheckOverride: pendingOverwrite.routeCheck });
                }}
                className="link-modal-danger rounded-2xl border border-amber-400/20 bg-amber-500/15 px-4 py-2 text-sm font-medium text-amber-100 hover:bg-amber-500/25"
              >
                Confirm overwrite
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
